import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import useSeo from "../hooks/useSeo";
import { apiFetch } from "../utils/api";

function paragraphs(content) {
  return String(content || "")
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
}

function ArticleLinks({ items }) {
  if (!items.length) {
    return <p className="text-gray-500">文章发布后，会列在这里。</p>;
  }
  return (
    <ul className="space-y-4">
      {items.map((item) => (
        <li key={item.slug}>
          <Link to={item.url} className="text-lg font-semibold text-rose-700 hover:underline">
            {item.title}
          </Link>
          {item.category ? (
            <span className="ml-2 text-sm text-gray-400">{item.category.name}</span>
          ) : null}
          {item.summary ? <p className="text-gray-600 mt-1">{item.summary}</p> : null}
        </li>
      ))}
    </ul>
  );
}

function Pager({ page, total, pageSize }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="flex gap-4 text-sm">
      {page > 1 ? <Link to={`?page=${page - 1}`}>上一页</Link> : null}
      {page < pages ? <Link to={`?page=${page + 1}`}>下一页</Link> : null}
    </div>
  );
}

function FeatureLinks() {
  const links = [
    ["/consultants", "婚恋咨询师"],
    ["/book-club", "晨读读书会"],
    ["/love-map", "爱情地图"],
    ["/courses", "婚恋学院"],
    ["/events", "交友活动"],
  ];
  return (
    <section className="space-y-2">
      <h2 className="text-xl font-bold">站内相关页面</h2>
      <ul className="flex flex-wrap gap-3 text-sm">
        {links.map(([to, label]) => (
          <li key={to}>
            <Link to={to} className="text-rose-700 hover:underline">
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function GrowthHomePage() {
  const [searchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") || 1));
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useSeo({
    title: "关系成长_倾听、冲突与信任 - AI婚恋平台",
    metaDescription: "关系成长文章：讨论亲密关系中的倾听、冲突、边界和信任，并说明如何配合网站上的练习工具。",
    path: page > 1 ? `/growth?page=${page}` : "/growth",
  });

  useEffect(() => {
    apiFetch(`/api/public/growth?page=${page}`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch(() => setError("内容暂时无法加载"));
  }, [page]);

  return (
    <div className="min-h-screen bg-rose-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-10 space-y-8">
        <h1 className="text-3xl font-bold">关系成长</h1>
        <p className="text-gray-700 leading-8">
          这里集中发布经过人工审核的关系成长文章，讨论倾听、冲突、边界、信任，以及怎样使用网站上的练习工具。
        </p>
        <p className="text-gray-700 leading-8">
          读书会里的会员发言不会直接公开。只有整理成独立文章、并经人工确认后，才会出现在这里。
        </p>
        {error ? <p>{error}</p> : null}
        {data?.categories?.length ? (
          <section className="space-y-2">
            <h2 className="text-xl font-bold">栏目</h2>
            <ul className="flex flex-wrap gap-3">
              {data.categories.map((item) => (
                <li key={item.slug}>
                  <Link to={item.url} className="text-rose-700 hover:underline">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <section className="space-y-3">
          <h2 className="text-xl font-bold">最新文章</h2>
          <ArticleLinks items={data?.items || []} />
          <Pager page={data?.page || page} total={data?.total || 0} pageSize={data?.page_size || 10} />
        </section>
        <FeatureLinks />
      </main>
      <Footer />
    </div>
  );
}

export function GrowthCategoryPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") || 1));
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useSeo({
    title: data?.category ? `${data.category.name} - 关系成长 - AI婚恋平台` : "关系成长",
    metaDescription: data?.category?.description || "关系成长栏目",
    path: page > 1 ? `/growth/category/${slug}?page=${page}` : `/growth/category/${slug}`,
    noindex: missing,
  });

  useEffect(() => {
    setMissing(false);
    apiFetch(`/api/public/growth/categories/${slug}?page=${page}`)
      .then(async (res) => {
        const json = await res.json();
        if (!res.ok) {
          setMissing(true);
          setData(null);
          return;
        }
        setData(json);
      })
      .catch(() => setMissing(true));
  }, [slug, page]);

  return (
    <div className="min-h-screen bg-rose-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-10 space-y-6">
        <p className="text-sm text-gray-500">
          <Link to="/growth">关系成长</Link>
        </p>
        {missing ? (
          <>
            <h1 className="text-3xl font-bold">页面不存在</h1>
            <p>这个栏目还没有公开文章。</p>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold">{data?.category?.name || "栏目"}</h1>
            <p className="text-gray-700 leading-8">{data?.category?.description}</p>
            <ArticleLinks items={data?.items || []} />
            <Pager page={data?.page || page} total={data?.total || 0} pageSize={data?.page_size || 10} />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

export function GrowthArticlePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const article = data?.article;

  useSeo({
    title: article ? `${article.seo_title || article.title} - AI婚恋平台` : "关系成长",
    metaDescription: article?.seo_description || article?.summary || "关系成长文章",
    path: `/growth/${slug}`,
    noindex: missing,
  });

  useEffect(() => {
    setMissing(false);
    apiFetch(`/api/public/growth/articles/${slug}`)
      .then(async (res) => {
        const json = await res.json();
        if (!res.ok) {
          setMissing(true);
          setData(null);
          return;
        }
        setData(json);
      })
      .catch(() => setMissing(true));
  }, [slug]);

  return (
    <div className="min-h-screen bg-rose-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-10 space-y-6">
        <p className="text-sm text-gray-500">
          <Link to="/growth">关系成长</Link>
          {article?.category ? (
            <>
              {" / "}
              <Link to={article.category.url}>{article.category.name}</Link>
            </>
          ) : null}
        </p>
        {missing ? (
          <>
            <h1 className="text-3xl font-bold">页面不存在</h1>
            <p>这篇文章没有公开，或地址已经改变。</p>
          </>
        ) : article ? (
          <>
            <h1 className="text-3xl font-bold">{article.title}</h1>
            {article.summary ? <p className="text-gray-700 leading-8">{article.summary}</p> : null}
            {article.published_at ? (
              <p className="text-sm text-gray-400">发布于 {article.published_at.slice(0, 10)}</p>
            ) : null}
            <article className="space-y-4 text-gray-800 leading-8">
              {paragraphs(article.content).map((block) => (
                <p key={block.slice(0, 24)} className="whitespace-pre-line">
                  {block}
                </p>
              ))}
            </article>
            {article.category ? (
              <p>
                <Link to={article.category.url} className="text-rose-700 hover:underline">
                  返回{article.category.name}
                </Link>
              </p>
            ) : null}
            {data.related?.length ? (
              <section className="space-y-3">
                <h2 className="text-xl font-bold">相关文章</h2>
                <ArticleLinks items={data.related} />
              </section>
            ) : null}
            <FeatureLinks />
          </>
        ) : (
          <p className="text-gray-400">加载中...</p>
        )}
      </main>
      <Footer />
    </div>
  );
}
